import React, { createContext, useContext, useEffect, useState } from "react";
import ExtensionIcon from "@mui/icons-material/Extension";
import GridOnIcon from "@mui/icons-material/GridOn";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import SpeedIcon from "@mui/icons-material/Speed";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { appendScreenshotLang } from "../../../lib/screenshotI18n";
import {
    getImageUrl,
    loadScreenshotCoverUrl,
    loadScreenshotGallery,
} from "../lib/screenshotApi";
import { screenshotPath } from "../routes";
import { getScreenshotScene } from "../screenshotScenes";
import { slidingShellBgForScene } from "../styles/screenshotStyles";

export const SQUARE_AD_W = 1200;
export const SQUARE_AD_H = 1200;
export const LANDSCAPE_AD_W = 1200;
export const LANDSCAPE_AD_H = 628;

/** @typedef {'landscape' | 'square'} GoogleAdVariant */

const AdVariantContext = createContext(/** @type {GoogleAdVariant} */ ("square"));
const AdLangContext = createContext("en-US");

function useIsSquare() {
    return useContext(AdVariantContext) === "square";
}

const TITLE_STACK =
    '"Segoe UI", "Arial Black", "Helvetica Neue", Impact, sans-serif';
const PHONE_W = 460;
const PHONE_H = 940;
const PHONE_W_SQ = 450;
const PHONE_H_SQ = 930;
const PUBLIC = process.env.PUBLIC_URL || "";
/** Themed Google Ads backdrops (landscape + square showcase). */
const THEME_BG = {
    tv: `${PUBLIC}/bg_images/sliding/tv-library.png`,
    funny: `${PUBLIC}/bg_images/sliding/funny-comic.png`,
    gorgeous: `${PUBLIC}/bg_images/sliding/gorgeous-velvet.png`,
};
/** Landscape/square shot 02 backdrop — Stylish Interiors puzzle. */
const INTERIOR_BG_STORY_ID = "156b02a0-959a-4b22-af6d-8d1bc9189f6a";

function useStoryCoverUrl(storyId) {
    const [url, setUrl] = useState("");
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const next = await loadScreenshotCoverUrl({ storyId });
            if (!cancelled && next) setUrl(next);
        })();
        return () => {
            cancelled = true;
        };
    }, [storyId]);
    return url;
}

function preloadImage(src) {
    return new Promise((resolve) => {
        if (!src) {
            resolve();
            return;
        }
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
    });
}

/** Puzzle cover URLs for a scene — no nested iframes (keeps Google Ads tab from OOMing). */
function useSceneCoverUrls(slug) {
    const [urls, setUrls] = useState([]);
    useEffect(() => {
        let cancelled = false;
        const scene = getScreenshotScene(slug);
        if (!scene) {
            setUrls([]);
            return undefined;
        }
        (async () => {
            try {
                const useGallery =
                    scene.layout === "twoColumnGrid" ||
                    (Array.isArray(scene.galleryPrepend) && scene.galleryPrepend.length > 0) ||
                    Boolean(scene.galleryTag);
                if (useGallery) {
                    const items = await loadScreenshotGallery(scene);
                    if (cancelled) return;
                    const next = items
                        .map((item) => getImageUrl(item?.group?.story))
                        .filter(Boolean);
                    await Promise.all(next.slice(0, 9).map(preloadImage));
                    if (!cancelled) setUrls(next);
                    return;
                }
                if (scene.storyId) {
                    const url = await loadScreenshotCoverUrl(scene);
                    if (cancelled) return;
                    if (url) await preloadImage(url);
                    if (!cancelled) setUrls(url ? [url] : []);
                    return;
                }
                if (!cancelled) setUrls([]);
            } catch {
                if (!cancelled) setUrls([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [slug]);
    return urls;
}

function lockOverflow() {
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
        document.documentElement.style.overflow = prevHtml;
        document.body.style.overflow = prevBody;
    };
}

function AdCanvas({ children, sx = {} }) {
    const variant = useContext(AdVariantContext);
    const isSquare = variant === "square";
    const width = isSquare ? SQUARE_AD_W : LANDSCAPE_AD_W;
    const height = isSquare ? SQUARE_AD_H : LANDSCAPE_AD_H;
    useEffect(() => lockOverflow(), []);
    return (
        <Box
            data-screenshot-frame={
                isSquare ? "sliding-square-ad" : "sliding-landscape-ad"
            }
            data-google-ad-square={isSquare ? "1" : undefined}
            data-google-ad-landscape={isSquare ? undefined : "1"}
            sx={{
                position: "fixed",
                inset: 0,
                width,
                height,
                overflow: "hidden",
                boxSizing: "border-box",
                zIndex: 1,
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

function ShellFill({ scene }) {
    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                inset: 0,
                zIndex: 0,
                background: slidingShellBgForScene(scene),
            }}
        />
    );
}

/**
 * Full-bleed themed photo backdrop for showcase ads.
 * @param {string} src
 * @param {number} [dim=0.28] — dark veil so phone + type stay readable
 * @param {string} [fallback] — CSS background while image loads / if missing
 */
function ThemeBackdrop({ src, dim = 0.28, fallback = "#0f172a" }) {
    return (
        <Box aria-hidden sx={{ position: "absolute", inset: 0, zIndex: 0, overflow: "hidden" }}>
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: fallback,
                    backgroundImage: src ? `url(${src})` : undefined,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: "saturate(1.05) contrast(1.02)",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: `rgba(2,6,23,${dim})`,
                    backgroundImage:
                        "radial-gradient(ellipse at 50% 45%, transparent 30%, rgba(2,6,23,0.35) 100%)",
                    pointerEvents: "none",
                }}
            />
        </Box>
    );
}

/** Full-bleed puzzle artwork (image, not nested iframe — avoids Chrome OOM). */
function SceneBleed({ slug, dim = 0.35, grid = false }) {
    const covers = useSceneCoverUrls(slug);
    const primary = covers[0];
    return (
        <Box aria-hidden sx={{ position: "absolute", inset: 0, zIndex: 0, overflow: "hidden" }}>
            {grid && covers.length > 1 ? (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gridTemplateRows: "repeat(3, 1fr)",
                        gap: "3px",
                    }}
                >
                    {covers.slice(0, 9).map((src) => (
                        <Box
                            key={src}
                            component="img"
                            src={src}
                            alt=""
                            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        />
                    ))}
                </Box>
            ) : primary ? (
                <Box
                    component="img"
                    src={primary}
                    alt=""
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        filter: "saturate(1.1) contrast(1.05)",
                    }}
                />
            ) : (
                <Box sx={{ position: "absolute", inset: 0, bgcolor: "#0f172a" }} />
            )}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: `rgba(2,6,23,${dim})`,
                    pointerEvents: "none",
                }}
            />
        </Box>
    );
}

function AdPhone({ rotate = 0, width, height, children, sx = {} }) {
    const isSquare = useIsSquare();
    const w = width ?? (isSquare ? PHONE_W_SQ : PHONE_W);
    const h = height ?? (isSquare ? PHONE_H_SQ : PHONE_H);
    return (
        <Box
            sx={{
                width: w,
                height: h,
                transform: rotate ? `rotate(${rotate}deg)` : "none",
                transformOrigin: "center center",
                borderRadius: "28px",
                p: "5px",
                background:
                    "linear-gradient(160deg, rgba(226,232,240,0.75) 0%, rgba(15,23,42,0.95) 48%, rgba(59,166,102,0.4) 100%)",
                boxShadow:
                    "0 22px 48px rgba(0,0,0,0.45), 0 0 0 1px rgba(167,243,208,0.28)",
                boxSizing: "border-box",
                flexShrink: 0,
                ...sx,
            }}
        >
            <Box
                sx={{
                    borderRadius: "23px",
                    overflow: "hidden",
                    bgcolor: "#0f172a",
                    height: "100%",
                    position: "relative",
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

const PORTRAIT_SHOT_W = 430;
const PORTRAIT_SHOT_H = 940;

/** Phone mockup. The screen is the live portrait screenshot for this shot. */
function ShotPhone({ slug, rotate = 0, width, height, sx }) {
    const lang = useContext(AdLangContext);
    const isSquare = useIsSquare();
    const w = width ?? (isSquare ? PHONE_W_SQ : PHONE_W);
    const h = height ?? (isSquare ? PHONE_H_SQ : PHONE_H);
    const innerW = Math.max(1, w - 10);
    const innerH = Math.max(1, h - 10);
    const scale = Math.max(innerW / PORTRAIT_SHOT_W, innerH / PORTRAIT_SHOT_H);
    const href = appendScreenshotLang(screenshotPath(slug), lang);
    const src =
        typeof window === "undefined"
            ? href
            : new URL(href, window.location.origin).toString();

    return (
        <AdPhone rotate={rotate} width={width} height={height} sx={sx}>
            <Box
                component="iframe"
                src={src}
                title={`Portrait ${slug}`}
                data-portrait-shot={slug}
                sx={{
                    position: "absolute",
                    left: "50%",
                    top: 0,
                    width: PORTRAIT_SHOT_W,
                    height: PORTRAIT_SHOT_H,
                    border: 0,
                    transform: `translateX(-50%) scale(${scale})`,
                    transformOrigin: "top center",
                    pointerEvents: "none",
                    background: "#f2f2f7",
                }}
            />
        </AdPhone>
    );
}

function AdHeadline({ title, subtitle, dark = false, sx = {} }) {
    const isSquare = useIsSquare();
    return (
        <Box sx={{ position: "relative", zIndex: 3, textAlign: "center", px: 3, ...sx }}>
            <Typography
                component="h1"
                sx={{
                    m: 0,
                    fontFamily: TITLE_STACK,
                    fontWeight: 900,
                    fontSize: isSquare ? 48 : 36,
                    lineHeight: 1.05,
                    letterSpacing: "0.03em",
                    color: dark ? "#0f172a" : "#f8fafc",
                    textShadow: dark ? "none" : "0 3px 18px rgba(2,6,23,0.65)",
                    whiteSpace: "pre-line",
                }}
            >
                {title}
            </Typography>
            {subtitle ? (
                <Typography
                    sx={{
                        m: 0,
                        mt: 1,
                        fontWeight: 600,
                        fontSize: isSquare ? 18 : 16,
                        color: dark ? "#475569" : "rgba(224,242,254,0.92)",
                        textShadow: dark ? "none" : "0 2px 10px rgba(2,6,23,0.5)",
                    }}
                >
                    {subtitle}
                </Typography>
            ) : null}
        </Box>
    );
}

function FloatBadge({ children, sx = {} }) {
    return (
        <Box
            sx={{
                position: "relative",
                zIndex: 3,
                px: 2,
                py: 1.25,
                borderRadius: 2,
                bgcolor: "rgba(15,23,42,0.84)",
                border: "1px solid rgba(110,231,183,0.45)",
                boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
                backdropFilter: "blur(8px)",
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

/** Phone + optional right callout — landscape scale/crop pattern. */
function PhoneWithCallout({
    scene,
    slug,
    title,
    subtitle,
    dark = false,
    callout,
    phoneRotate = -3,
    /** Optional themed photo backdrop (replaces plain shell fill). */
    themeBg = null,
    themeDim = 0.28,
    themeFallback = "#0f172a",
}) {
    const isSquare = useIsSquare();
    return (
        <AdCanvas>
            {typeof themeBg === "string" ? (
                <ThemeBackdrop src={themeBg} dim={themeDim} fallback={themeFallback} />
            ) : (
                <ShellFill scene={scene} />
            )}
            <AdHeadline
                title={title}
                subtitle={subtitle}
                dark={dark}
                sx={{
                    position: "absolute",
                    top: isSquare ? 36 : 16,
                    left: 0,
                    right: 0,
                    pt: 0,
                    zIndex: 4,
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    left: isSquare ? 56 : 100,
                    top: "54%",
                    transform: "translateY(-50%)",
                    zIndex: 3,
                }}
            >
                <ShotPhone
                    slug={slug}
                    rotate={phoneRotate}
                    width={isSquare ? 460 : 480}
                    height={isSquare ? 940 : 980}
                />
            </Box>
            {callout ? (
                <Box
                    sx={{
                        position: "absolute",
                        right: isSquare ? 48 : 40,
                        top: isSquare ? 360 : "52%",
                        transform: isSquare ? "none" : "translateY(-50%)",
                        zIndex: 4,
                        width: isSquare ? 420 : 460,
                    }}
                >
                    {callout}
                </Box>
            ) : null}
        </AdCanvas>
    );
}

function ShowcaseClassic({ scene }) {
    const isSquare = useIsSquare();
    return (
        <AdCanvas>
            <ShellFill scene={scene} />
            <AdHeadline
                title={isSquare ? "CLASSIC 4×4\nPUZZLES" : "CLASSIC 4×4 PUZZLES"}
                subtitle="Clean grids. Smooth slides. Pure focus."
                sx={{
                    position: "absolute",
                    top: isSquare ? 140 : 20,
                    left: 0,
                    right: 0,
                    pt: 0,
                    zIndex: 4,
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    // Square: sit phones in the vertical middle (bleed past bottom edge)
                    top: isSquare ? 680 : "54%",
                    transform: "translate(-50%, -50%)",
                    display: "flex",
                    gap: isSquare ? 3 : 3.5,
                    zIndex: 3,
                }}
            >
                <ShotPhone
                    slug="01"
                    rotate={-10}
                    width={isSquare ? 400 : 380}
                    height={isSquare ? 840 : 780}
                />
                <ShotPhone
                    slug="02"
                    rotate={9}
                    width={isSquare ? 400 : 380}
                    height={isSquare ? 840 : 780}
                />
            </Box>
        </AdCanvas>
    );
}

function ShowcaseInterior({ scene }) {
    const themeBg = useStoryCoverUrl(INTERIOR_BG_STORY_ID);
    return (
        <PhoneWithCallout
            scene={scene}
            slug="03"
            title="STYLISH INTERIORS"
            subtitle="Beautiful furniture and home interiors."
            themeBg={themeBg}
            themeDim={0.32}
            themeFallback="#44403c"
            callout={
                <FloatBadge sx={{ py: 2.5 }}>
                    <TouchAppIcon sx={{ color: "#6ee7b7", fontSize: 40 }} />
                    <Typography
                        sx={{
                            mt: 1,
                            fontFamily: TITLE_STACK,
                            fontWeight: 900,
                            fontSize: 26,
                            color: "#ecfdf5",
                        }}
                    >
                        Smooth slides
                    </Typography>
                    <Typography sx={{ mt: 0.75, fontSize: 14, color: "rgba(226,232,240,0.88)" }}>
                        Tap tiles and glide into place — satisfying every move.
                    </Typography>
                </FloatBadge>
            }
        />
    );
}

function ShowcaseGhost({ scene }) {
    return (
        <PhoneWithCallout
            scene={scene}
            slug="06"
            title="YOUR FAVORITE SCENES"
            subtitle="Slide through iconic television sets."
            themeBg={THEME_BG.tv}
            themeDim={0.32}
            themeFallback="#292524"
            callout={
                <FloatBadge
                    sx={{
                        py: 2.5,
                        bgcolor: "rgba(255,255,255,0.96)",
                        border: "1px solid rgba(148,163,184,0.45)",
                    }}
                >
                    <LightbulbOutlinedIcon sx={{ color: "#3BA666", fontSize: 40 }} />
                    <Typography sx={{ mt: 1, fontWeight: 900, fontSize: 26, color: "#0f172a" }}>
                        Ghost Guide
                    </Typography>
                    <Typography sx={{ mt: 0.75, fontSize: 14, color: "#475569" }}>
                        Soft hints light the next move when you need a nudge.
                    </Typography>
                </FloatBadge>
            }
        />
    );
}

function ShowcaseGiant({ scene }) {
    const isSquare = useIsSquare();
    return (
        <AdCanvas>
            {/* Fallback shell so edges never show empty canvas */}
            <ShellFill scene={scene} />
            {/* Scale bleed so landscape/square edges stay filled (no empty left gutter). */}
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 0,
                    overflow: "hidden",
                    "& > *": {
                        transform: isSquare ? "scale(1.12)" : "scale(1.28)",
                        transformOrigin: isSquare ? "center center" : "left center",
                    },
                }}
            >
                <SceneBleed slug="04" dim={isSquare ? 0.14 : 0.12} />
            </Box>
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    background: isSquare
                        ? "linear-gradient(135deg, transparent 28%, rgba(2,6,23,0.62) 78%)"
                        : "linear-gradient(90deg, rgba(2,6,23,0.28) 0%, rgba(2,6,23,0.18) 42%, rgba(2,6,23,0.55) 100%)",
                    pointerEvents: "none",
                }}
            />
            <AdHeadline
                title={isSquare ? "GIANT 6×6\nEXPERT GRID" : "GIANT 6x6 EXPERT GRID"}
                sx={{
                    position: "absolute",
                    top: isSquare ? 48 : 18,
                    left: 0,
                    right: 0,
                    pt: 0,
                    zIndex: 4,
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    // Bleed phone past the left edge so the left half stays filled.
                    left: isSquare ? -40 : -60,
                    top: isSquare ? "58%" : "52%",
                    transform: "translateY(-50%)",
                    zIndex: 3,
                }}
            >
                <ShotPhone
                    slug="05"
                    rotate={-2}
                    width={isSquare ? 520 : 560}
                    height={isSquare ? 1060 : 1140}
                />
            </Box>
            <FloatBadge
                sx={{
                    position: "absolute",
                    right: 40,
                    top: isSquare ? 280 : "50%",
                    transform: isSquare ? "none" : "translateY(-50%)",
                    width: isSquare ? 400 : 420,
                    textAlign: "center",
                    py: 3,
                    zIndex: 4,
                }}
            >
                <GridOnIcon sx={{ fontSize: 44, color: "#6ee7b7" }} />
                <Typography
                    sx={{
                        mt: 1,
                        fontFamily: TITLE_STACK,
                        fontWeight: 900,
                        fontSize: 32,
                        color: "#ecfdf5",
                    }}
                >
                    6×6 GRID
                </Typography>
                <Typography sx={{ mt: 0.75, fontSize: 14, color: "rgba(226,232,240,0.88)" }}>
                    Intricate Museum Library. Expert challenge.
                </Typography>
            </FloatBadge>
        </AdCanvas>
    );
}

function ShowcaseFunny({ scene }) {
    return (
        <PhoneWithCallout
            scene={scene}
            slug="04"
            title="CRACK UP"
            subtitle="Hilarious sliding puzzle fun."
            dark
            themeBg={THEME_BG.funny}
            themeDim={0.12}
            themeFallback="#f5f0e6"
            callout={
                <FloatBadge
                    sx={{
                        py: 2.5,
                        textAlign: "center",
                        bgcolor: "rgba(15,23,42,0.88)",
                        border: "1px solid rgba(251,191,36,0.45)",
                    }}
                >
                    <SpeedIcon sx={{ color: "#fbbf24", fontSize: 42 }} />
                    <Typography
                        sx={{
                            mt: 1,
                            fontFamily: TITLE_STACK,
                            fontWeight: 900,
                            fontSize: 26,
                            color: "#fde68a",
                        }}
                    >
                        Quick laughs
                    </Typography>
                    <Typography sx={{ mt: 0.75, fontSize: 14, color: "rgba(226,232,240,0.88)" }}>
                        Funny scenes that keep every slide entertaining.
                    </Typography>
                </FloatBadge>
            }
        />
    );
}

function ShowcaseGallery({ scene }) {
    const isSquare = useIsSquare();
    const pills = [
        { label: "Offline Play", Icon: WifiOffIcon },
        { label: "1000+ Puzzles", Icon: PhotoLibraryIcon },
        { label: "Smooth Slides", Icon: ExtensionIcon },
        { label: "No Time Limits", Icon: SpeedIcon },
    ];
    return (
        <AdCanvas>
            <ShellFill scene={scene} />
            <SceneBleed slug="06" dim={0.08} grid />
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 2,
                    background:
                        "linear-gradient(180deg, rgba(2,6,23,0.55) 0%, transparent 28%, transparent 70%, rgba(2,6,23,0.6) 100%)",
                    pointerEvents: "none",
                }}
            />
            <Stack
                direction="row"
                spacing={1.25}
                justifyContent="center"
                sx={{
                    position: "absolute",
                    left: 32,
                    right: 32,
                    top: isSquare ? 28 : 18,
                    zIndex: 5,
                }}
            >
                {pills.map(({ label, Icon }) => (
                    <FloatBadge key={label} sx={{ py: 0.9, px: 1.5, flex: 1 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
                            <Icon sx={{ color: "#6ee7b7", fontSize: 18 }} />
                            <Typography sx={{ fontWeight: 800, fontSize: 13, color: "#e0f2fe" }}>
                                {label}
                            </Typography>
                        </Stack>
                    </FloatBadge>
                ))}
            </Stack>
            <AdHeadline
                title="1000+ PUZZLES"
                subtitle="A huge collection of stunning themes to play."
                sx={{
                    position: "absolute",
                    bottom: isSquare ? 48 : 28,
                    left: 0,
                    right: 0,
                    pt: 0,
                    zIndex: 5,
                }}
            />
        </AdCanvas>
    );
}

function ShowcaseTravel({ scene }) {
    return (
        <PhoneWithCallout
            scene={scene}
            slug="07"
            title="EXPLORE THE WORLD"
            subtitle="Beautiful scenes from around the globe."
            callout={
                <FloatBadge sx={{ py: 2.5 }}>
                    <PhotoLibraryIcon sx={{ color: "#5eead4", fontSize: 40 }} />
                    <Typography
                        sx={{
                            mt: 1,
                            fontFamily: TITLE_STACK,
                            fontWeight: 900,
                            fontSize: 26,
                            color: "#ecfdf5",
                        }}
                    >
                        Travel scenes
                    </Typography>
                    <Typography sx={{ mt: 0.75, fontSize: 14, color: "rgba(226,232,240,0.88)" }}>
                        Landmarks and cultures in every tile.
                    </Typography>
                </FloatBadge>
            }
        />
    );
}

function ShowcaseGorgeous({ scene }) {
    return (
        <PhoneWithCallout
            scene={scene}
            slug="08"
            title="STUNNING VIEWS"
            subtitle="Gorgeous sliding puzzles to solve."
            themeBg={THEME_BG.gorgeous}
            themeDim={0.26}
            themeFallback="#064e3b"
            callout={
                <FloatBadge
                    sx={{
                        py: 2.5,
                        textAlign: "center",
                        background:
                            "linear-gradient(165deg, rgba(6,78,59,0.92) 0%, rgba(15,23,42,0.85) 100%)",
                        border: "1px solid rgba(212,175,55,0.45)",
                        boxShadow: "0 16px 36px rgba(0,0,0,0.4)",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: TITLE_STACK,
                            fontWeight: 900,
                            fontSize: 28,
                            color: "#ecfdf5",
                        }}
                    >
                        HD scenery
                    </Typography>
                    <Typography sx={{ mt: 0.75, fontSize: 14, color: "rgba(226,232,240,0.88)" }}>
                        Crisp photography that rewards every solved board.
                    </Typography>
                </FloatBadge>
            }
        />
    );
}

/**
 * Sliding Puzzle Quest Google Ads showcase creatives (square 1200×1200 or landscape 1200×628).
 */
export default function SlidingShowcaseGoogleAdCreative({
    scene,
    lang = "en-US",
    variant = "square",
}) {
    const slug = scene?.slug || "01";
    let content = null;
    switch (slug) {
        case "01":
            content = <ShowcaseClassic scene={scene} />;
            break;
        case "02":
            content = <ShowcaseInterior scene={scene} />;
            break;
        case "03":
            content = <ShowcaseFunny scene={scene} />;
            break;
        case "04":
            content = <ShowcaseGiant scene={scene} />;
            break;
        case "05":
            content = <ShowcaseGhost scene={scene} />;
            break;
        case "06":
            content = <ShowcaseGallery scene={scene} />;
            break;
        case "07":
            content = <ShowcaseTravel scene={scene} />;
            break;
        case "08":
            content = <ShowcaseGorgeous scene={scene} />;
            break;
        default:
            content = <ShowcaseClassic scene={scene} />;
            break;
    }

    return (
        <AdVariantContext.Provider value={variant === "landscape" ? "landscape" : "square"}>
            <AdLangContext.Provider value={lang}>{content}</AdLangContext.Provider>
        </AdVariantContext.Provider>
    );
}
